import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  _AbcGridComponent,
  _AbcContextMenuComponent,
  _AbcCheckBoxComponent,
  _AbcGridColumn,
  AbcDateRangeBoxComponent,
  _AbcButtonComponent,
  AbcTextBoxComponent,
  AbcLoadPanelComponent,
} from '@barthauer/abc/ui-components';
import {
  DxButtonModule,
  DxDataGridModule,
  DxCheckBoxModule,
  DxPopoverModule,
  DxScrollViewModule,
  DxTooltipModule,
  DxDateRangeBoxModule,
} from 'devextreme-angular';
import { SharedModule } from '../../../../shared/shared.module';
import { AuditConfig, AuditRow, HistoryService } from '../../../../services/history.service';
import { MeasureService } from '../../measure.service';
import { DataService, ObjectKeys } from '../../../../services/data.service';
import { finalize, forkJoin } from 'rxjs';
import { AbcCore } from '@barthauer/abc/core';
import { ValueChangedEvent } from 'devextreme/ui/text_box';

@Component({
  selector: 'iup-aims-measure-change-history',
  imports: [
    DxButtonModule,
    _AbcGridComponent,
    DxDataGridModule,
    DxCheckBoxModule,
    DxPopoverModule,
    DxScrollViewModule,
    DxTooltipModule,
    SharedModule,
    CommonModule,
    _AbcButtonComponent,
    AbcTextBoxComponent,
    DxDateRangeBoxModule,
    AbcLoadPanelComponent,
  ],
  templateUrl: './measure-change-history.component.html',
  styleUrl: './measure-change-history.component.scss',
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasureChangeHistoryComponent implements OnInit {
  private historyService = inject(HistoryService);
  private measureService = inject(MeasureService);
  private dataService = inject(DataService);
  private abcCoreService = inject(AbcCore);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('historyOfChangesGrid', { static: false })
  historyOfChangesGrid: _AbcGridComponent;
  @ViewChild('searchBox', { static: false })
  searchBox: AbcTextBoxComponent;
  @ViewChild('dateRangeBox', { static: false })
  dateRangeBox: AbcDateRangeBoxComponent;
  protected measureId = computed(() => this.measureService.currentMeasureId());
  protected translator = this.dataService.res;
  protected historyOfChanges: AuditRow[] = [];
  protected filtredHistoryOfChanges: AuditRow[] = [];
  protected dateRange: [string, string] = ['', ''];
  startDate: Date = new Date(new Date().setDate(new Date().getDate() - 30));
  endDate: Date = new Date();
  protected isLoading = false;
  protected columnsHeader: _AbcGridColumn[] = [
    {
      caption: this.dataService.res('Audit-ObjectType'),
      dataField: 'object',
      alignment: 'center',
      visibleIndex: 0,
    },

    {
      caption: this.dataService.res('Audit-EventType'),
      dataField: 'eventType',
      alignment: 'center',
      visibleIndex: 1,
    },
    {
      caption: this.dataService.res('Audit-ObjectDesignation'),
      dataField: 'objectDesignation',
      alignment: 'center',
      visibleIndex: 2,
    },

    {
      caption: this.dataService.res('Audit-User'),
      dataField: 'user',
      alignment: 'center',
      visibleIndex: 3,
    },
    {
      caption: this.dataService.res('Audit-ChangeDate'),
      dataField: 'changeDate',
      dataType: 'date',
      alignment: 'center',
      visibleIndex: 4,
    },
    {
      caption: this.dataService.res('Audit-Property'),
      dataField: 'property',
      alignment: 'center',
      visibleIndex: 5,
    },
    {
      caption: this.dataService.res('Audit-OldValue'),
      dataField: 'oldValue',
      alignment: 'left',
      visibleIndex: 6,
      customizeText: (e) => (e.value != null ? String(e.value) : '-'),
    },
    {
      caption: this.dataService.res('Audit-NewValue'),
      dataField: 'newValue',
      alignment: 'left',
      visibleIndex: 7,
      customizeText: (e) => (e.value != null ? String(e.value) : '-'),
    },
  ];

  private readonly measureColumns = [
    this.dataService.coreDataService.createQueryColumn('Id', 'Id'),
    this.dataService.coreDataService.createQueryColumn('Designation', 'Designation'),
    this.dataService.coreDataService.createQueryColumn('SeqNr', 'SeqNr'),
    this.dataService.coreDataService.createQueryColumn('Goal', 'Goal'),
    this.dataService.coreDataService.createQueryColumn('Status', 'Status'),
    this.dataService.coreDataService.createQueryColumn('Summary', 'Summary'),
    this.dataService.coreDataService.createQueryColumn('CompletionDate', 'CompletionDate'),
    this.dataService.coreDataService.createQueryColumn('StartDate', 'StartDate'),
    this.dataService.coreDataService.createQueryColumn('EndDate', 'EndDate'),
    this.dataService.coreDataService.createQueryColumn('OriginalTargetDate', 'OriginalTargetDate'),
    this.dataService.coreDataService.createQueryColumn('ReleaseDate', 'ReleaseDate'),
    this.dataService.coreDataService.createQueryColumn('PrevDect', 'PrevDect'),
    this.dataService.coreDataService.createQueryColumn('Impact', 'Impact'),
    this.dataService.coreDataService.createQueryColumn('Benefit', 'Benefit'),
    this.dataService.coreDataService.createQueryColumn('Cost', 'Cost'),
    this.dataService.coreDataService.createQueryColumn('Effort', 'Effort'),
    this.dataService.coreDataService.createQueryColumn('TotalScore', 'TotalScore'),
    this.dataService.coreDataService.createQueryColumn('Concept', 'Concept'),
    this.dataService.coreDataService.createQueryColumn('InitialSituation', 'InitialSituation'),
    this.dataService.coreDataService.createQueryColumn('OrgaTech', 'OrgaTech'),
    this.dataService.coreDataService.createQueryColumn('Interval', 'Interval'),
    this.dataService.coreDataService.createQueryColumn('IntervalKind', 'IntervalKind'),
    this.dataService.coreDataService.createQueryColumn('Kind', 'Kind'),
    this.dataService.coreDataService.createQueryColumn('DefenseMeasures', 'DefenseMeasures'),
    this.dataService.coreDataService.createQueryColumn('Dependencies', 'Dependencies'),
    this.dataService.coreDataService.createQueryColumn('ExpectedResults', 'ExpectedResults'),

    // references
    this.dataService.coreDataService.createQueryColumn('ref:IdRefPersonAreaManager', 'ManagerName'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefPersonResponsible', 'measureResponsible'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefPersonConsultant', 'measureConsultant'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefPersonSponsor', 'measureSponsor'),
    // versioning fields
    this.dataService.coreDataService.createQueryColumn('SysUserNameInsert', 'SysUserNameInsert'),
    this.dataService.coreDataService.createQueryColumn('SysUserNameUpdate', 'SysUserNameUpdate'),
    this.dataService.coreDataService.createQueryColumn('SysDateInsert', 'SysDateInsert'),
    this.dataService.coreDataService.createQueryColumn('SysDateUpdate', 'SysDateUpdate'),
    this.dataService.coreDataService.createQueryColumn('VersionStart()', 'ValidFrom'),
    this.dataService.coreDataService.createQueryColumn('VersionEnd()', 'ValidTo'),
  ];

  private readonly measureTaskColumns = [
    this.dataService.coreDataService.createQueryColumn('Id', 'Id'),
    this.dataService.coreDataService.createQueryColumn('ParentId', 'ParentId'),
    this.dataService.coreDataService.createQueryColumn('ObjectType', 'ObjectType'),
    this.dataService.coreDataService.createQueryColumn('ParentObjectType', 'ParentObjectType'),

    this.dataService.coreDataService.createQueryColumn('Subject', 'Subject'),
    this.dataService.coreDataService.createQueryColumn('Description', 'Description'),
    this.dataService.coreDataService.createQueryColumn('Comment', 'Comment'),

    this.dataService.coreDataService.createQueryColumn('Status', 'Status'),
    this.dataService.coreDataService.createQueryColumn('Priority', 'Priority'),
    this.dataService.coreDataService.createQueryColumn('Type', 'Type'),

    this.dataService.coreDataService.createQueryColumn('StartDate', 'StartDate'),
    this.dataService.coreDataService.createQueryColumn('StartTime', 'StartTime'),
    this.dataService.coreDataService.createQueryColumn('EndDate', 'EndDate'),
    this.dataService.coreDataService.createQueryColumn('EndTime', 'EndTime'),
    this.dataService.coreDataService.createQueryColumn('FinishedOn', 'FinishedOn'),

    this.dataService.coreDataService.createQueryColumn('Duration', 'Duration'),
    this.dataService.coreDataService.createQueryColumn('Efficiency', 'Efficiency'),
    this.dataService.coreDataService.createQueryColumn('DegreeOfImplementation', 'DegreeOfImplementation'),

    this.dataService.coreDataService.createQueryColumn('Interval', 'Interval'),
    this.dataService.coreDataService.createQueryColumn('IntervalKind', 'IntervalKind'),

    this.dataService.coreDataService.createQueryColumn('TargetMaturityLevel', 'TargetMaturityLevel'),
    this.dataService.coreDataService.createQueryColumn('TotalTimeExecuted', 'TotalTimeExecuted'),
    this.dataService.coreDataService.createQueryColumn('TotalTimeRequired', 'TotalTimeRequired'),

    // References
    this.dataService.coreDataService.createQueryColumn('ref:IdRefDepartment', 'Department'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefPersonResubmissionFor', 'ResubPerson'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefResponsible', 'Responsible'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefxmtMeasure', 'Measure'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefxmtMeasureTask', 'MeasureTaskType'),

    // System fields (important for audit)
    this.dataService.coreDataService.createQueryColumn('SysDateInsert', 'SysDateInsert'),
    this.dataService.coreDataService.createQueryColumn('SysDateUpdate', 'SysDateUpdate'),
    this.dataService.coreDataService.createQueryColumn('SysUserNameInsert', 'SysUserNameInsert'),
    this.dataService.coreDataService.createQueryColumn('SysUserNameUpdate', 'SysUserNameUpdate'),

    // Versioning fields
    this.dataService.coreDataService.createQueryColumn('VersionStart()', 'ValidFrom'),
    this.dataService.coreDataService.createQueryColumn('VersionEnd()', 'ValidTo'),
  ];

  private readonly measureProgressColumns = [
    this.dataService.coreDataService.createQueryColumn('Id', 'Id'),
    this.dataService.coreDataService.createQueryColumn('Decision', 'Decision'),
    this.dataService.coreDataService.createQueryColumn('MaturityLevel', 'MaturityLevel'),
    this.dataService.coreDataService.createQueryColumn('NextSteps', 'NextSteps'),
    this.dataService.coreDataService.createQueryColumn('Progress', 'Progress'),
    this.dataService.coreDataService.createQueryColumn('ProgressDate', 'ProgressDate'),
    this.dataService.coreDataService.createQueryColumn('SysTimeInsert', 'SysTimeInsert'),
    this.dataService.coreDataService.createQueryColumn('ProgressPercentage', 'ProgressPercentage'),
    this.dataService.coreDataService.createQueryColumn('RiskAndChance', 'RiskAndChance'),
    this.dataService.coreDataService.createQueryColumn('SysUserNameInsert', 'SysUserNameInsert'),
    this.dataService.coreDataService.createQueryColumn('SysUserNameUpdate', 'SysUserNameUpdate'),
    this.dataService.coreDataService.createQueryColumn('SysDateInsert', 'SysDateInsert'),
    this.dataService.coreDataService.createQueryColumn('SysDateUpdate', 'SysDateUpdate'),
    this.dataService.coreDataService.createQueryColumn('VersionStart()', 'ValidFrom'),
    this.dataService.coreDataService.createQueryColumn('VersionEnd()', 'ValidTo'),
  ];

  private readonly measureStaffMemberColumns = [
    this.dataService.coreDataService.createQueryColumn('Id', 'Id'),
    this.dataService.coreDataService.createQueryColumn('ref:IdRefPerson', 'FullName'),
    this.dataService.coreDataService.createQueryColumn('Measure.Designation', 'MeasureName'),

    // versioning fields
    this.dataService.coreDataService.createQueryColumn('SysUserNameInsert', 'SysUserNameInsert'),
    this.dataService.coreDataService.createQueryColumn('SysUserNameUpdate', 'SysUserNameUpdate'),
    this.dataService.coreDataService.createQueryColumn('SysDateInsert', 'SysDateInsert'),
    this.dataService.coreDataService.createQueryColumn('SysDateUpdate', 'SysDateUpdate'),
    this.dataService.coreDataService.createQueryColumn('VersionStart()', 'ValidFrom'),
    this.dataService.coreDataService.createQueryColumn('VersionEnd()', 'ValidTo'),
  ];

  private readonly measureAuditConfig: AuditConfig = {
    objectIdField: 'Id',

    objectType: this.translator('Ubt-CreateMeasureTask-Measure'),

    designationField: 'Designation',

    validFromField: 'ValidFrom',
    validToField: 'ValidTo',

    insertUserField: 'SysUserNameInsert',
    updateUserField: 'SysUserNameUpdate',
    insertDateField: 'SysDateInsert',
    updateDateField: 'SysDateUpdate',

    ignoredFields: ['SysDateInsert', 'SysDateUpdate', 'Id', 'SeqNr'],
    fieldLabels: {
      Designation: this.translator('Ubt-Measure-List-Designation'),
      StartDate: this.translator('Ubt-Measure-List-StartDate'),
      EndDate: this.translator('Ubt-Measure-List-EndDate'),
      InitialSituation: this.translator('Ubt-Measure-List-InitialSituation'),
      Goal: this.translator('Ubt-CreateMeasure-Objective'),
      Concept: this.translator('Ubt-Html-Editor-Concept'),
      ExpectedResults: this.translator('Ubt-MeasureDetail-Expected-Results'),
      DefenseMeasures: this.translator('Ubt-MeasureDetail-Defense-Measures'),
      Dependencies: this.translator('Ubt-MeasureDetail-Dependencies'),
      measureResponsible: this.translator('Ubt-CreateMeasure-Responsible'),
      ManagerName: this.translator('Ubt-CreateMeasure-AreaManager'),
    },
  };
  private readonly measureTasksAuditConfig: AuditConfig = {
    objectIdField: 'Id',

    objectType: this.translator('Ubt-MeasureDetail-Milestones-Tasks'),

    designationField: 'Subject',

    validFromField: 'ValidFrom',
    validToField: 'ValidTo',

    insertUserField: 'SysUserNameInsert',
    updateUserField: 'SysUserNameUpdate',
    insertDateField: 'SysDateInsert',
    updateDateField: 'SysDateUpdate',

    ignoredFields: ['SysDateInsert', 'SysDateUpdate', 'Id', 'ParentId'],
    fieldLabels: {
      Subject: this.translator('Ubt-MeasureTask-Label'),
      StartDate: this.translator('Ubt-Measure-List-StartDate'),
      EndDate: this.translator('Ubt-Measure-List-EndDate'),
      Responsible: this.translator('Ubt-CreateMeasure-Responsible'),
      Status: this.translator('Ubt-Measure-Detail-Status-Label'),
      DegreeOfImplementation: this.translator('Ubt-MeasureTask-DegreeOfImplementation'),
      TotalTimeRequired: this.translator('Ubt-CreateMeasureTask-Total-Time'),
      Comment: 'Notizen',
    },
  };
  private readonly measureProgressAuditConfig: AuditConfig = {
    objectIdField: 'Id',

    objectType: this.translator('Ubt-MeasureDetail-Status-Report'),

    designationField: 'MaturityLevel',

    validFromField: 'ValidFrom',
    validToField: 'ValidTo',

    insertUserField: 'SysUserNameInsert',
    updateUserField: 'SysUserNameUpdate',
    insertDateField: 'SysDateInsert',
    updateDateField: 'SysDateUpdate',

    ignoredFields: ['SysDateInsert', 'SysDateUpdate', 'Id', 'ParentId'],
    fieldLabels: {
      Progress: this.translator('Ubt-MeasureDetail-Progress'),
      ProgressDate: this.translator('Ubt-MeasureDetail-ProgressDate'),
      NextSteps: this.translator('Ubt-MeasureDetail-Next-Steps'),
      RiskAndChance: this.translator('Ubt-MeasureDetail-Risks-Opportunities'),
      Decision: this.translator('Ubt-MeasureDetail-Decision-Clarification'),
      ProgressPercentage: this.translator('Ubt-MeasureDetail-Chart-Progress'),
    },
  };
  private readonly measureStaffMemberAuditConfig: AuditConfig = {
    objectIdField: 'Id',

    objectType: this.translator('Ubt-CreateMeasure-Members'),

    designationField: 'FullName',

    validFromField: 'ValidFrom',
    validToField: 'ValidTo',

    insertUserField: 'SysUserNameInsert',
    updateUserField: 'SysUserNameUpdate',
    insertDateField: 'SysDateInsert',
    updateDateField: 'SysDateUpdate',

    ignoredFields: ['SysDateInsert', 'SysDateUpdate', 'Id', 'ParentId'],
    fieldLabels: {
      FullName: this.translator('Ubt-MeasureDetail-Progress'),
    },
  };

  constructor() {}

  ngOnInit() {
    this.initializeDateRange();
    this.loadHistory();
  }

  protected currentValueChanged({ value: [startDate, endDate] }) {
    this.dateRange = [
      startDate?.toISOString().split('T')[0] ?? this.startDate.toISOString().split('T')[0],
      endDate?.toISOString().split('T')[0] ?? this.endDate.toISOString().split('T')[0],
    ];
    this.loadHistory();
  }
  protected resetFilters() {
    this.initializeDateRange();
    this.loadHistory();
  }
  protected loadHistory() {
    this.searchBox?.instance.clear();
    this.isLoading = true;
    const measureHistory$ = this.historyService.getHistory(
      ObjectKeys.MEASURE,
      this.dateRange[0],
      this.dateRange[1],
      this.measureId(),
      this.measureColumns,
      this.measureAuditConfig,
    );

    const measureTaskHistory$ = this.historyService.getHistory(
      ObjectKeys.MEASURETASK,
      this.dateRange[0],
      this.dateRange[1],
      this.measureId(),
      this.measureTaskColumns,
      this.measureTasksAuditConfig,
      true,
    );

    const measureProgressHistory$ = this.historyService.getHistory(
      ObjectKeys.MEASUREPROGRESS,
      this.dateRange[0],
      this.dateRange[1],
      this.measureId(),
      this.measureProgressColumns,
      this.measureProgressAuditConfig,
      true,
    );

    const measureStaffMemberHistory$ = this.historyService.getHistory(
      ObjectKeys.RESOURCE,
      this.dateRange[0],
      this.dateRange[1],
      this.measureId(),
      this.measureStaffMemberColumns,
      this.measureStaffMemberAuditConfig,
      true,
    );
    this.historyOfChangesGrid?.instance.beginCustomLoading('Loading...');
    forkJoin([measureHistory$, measureTaskHistory$, measureStaffMemberHistory$, measureProgressHistory$])
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.historyOfChangesGrid?.instance.endCustomLoading();
        }),
      )
      .subscribe({
        next: ([measureHistory, measureTaskHistory, measureProgressHistory]) => {
          this.historyOfChanges = [...measureHistory, ...measureTaskHistory, ...measureProgressHistory].sort((a, b) => {
            const bt = b && b.changeDate ? new Date(b.changeDate).getTime() : 0;
            const at = a && a.changeDate ? new Date(a.changeDate).getTime() : 0;
            return bt - at;
          });
          this.filtredHistoryOfChanges = this.historyOfChanges;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.abcCoreService.error.publish(error);
          console.error(error);
        },
      });
  }

  private initializeDateRange() {
    const endDate = this.endDate.toISOString().split('T')[0];
    const startDate = this.startDate.toISOString().split('T')[0];
    this.dateRange = [startDate, endDate];
    this.dateRangeBox?.instance.reset();
  }

  protected globalSearch(searchTerm: ValueChangedEvent) {
    if (!searchTerm?.value) {
      this.filtredHistoryOfChanges = this.historyOfChanges;
    }
    const lowerSearch = searchTerm?.value.toLowerCase();
    this.filtredHistoryOfChanges = this.filtredHistoryOfChanges.filter((item) =>
      Object.values(item).some((value) => String(value).toLowerCase().includes(lowerSearch)),
    );
    this.historyOfChangesGrid?.instance.refresh();
  }
}
